import { defineReactiveModel, Persistable, hasMany, belongsTo } from "vue-app-utils";
import Model from "@/models/model";
import { goalsBySquadId, isDecided, winningSquadId, findScheduleEventByEncounter, findScheduleEventByBracketSlot } from "@/utils/race-utils";

const Race = {
  extends: Model,
  mixins: [
    Persistable,
    hasMany("games", {
      orderBy: ["createdAt"]
    }),
    hasMany("memberSnapshots"),
    belongsTo("league")
  ],
  data () {
    return {
      squadIds: [],
      encounterNumber: 1,
      scoringLock: false,
      bracketSlot: null,
      homeSeed: null,
      awaySeed: null,
      ratingsSyncedAt: null
    };
  },
  computed: {
    activeGames () {
      return this.games.filter(g => g.active);
    },
    inactiveGames () {
      return this.games.filter(g => !g.active);
    },
    isBracketRace () {
      return !!this.bracketSlot;
    },
    squads () {
      if (this.isBracketRace) {
        const ids = this.league.bracketSquads[this.bracketSlot] || [undefined, undefined];
        return ids.map(id => this.league.squads.find(s => s.id === id)).filter(Boolean);
      }
      return this.squadIds
        .map(id => this.league.squads.find(s => s.id === id))
        .filter(Boolean);
    },
    // The squad ids that actually form this race's matchup: persisted for round-robin races,
    // computed from the bracket squads for bracket races.
    actualSquadIds () {
      if (this.isBracketRace) {
        return this.squads.map(s => s.id);
      }
      return this.squadIds;
    },
    homeSquad () {
      return this.squads[0];
    },
    awaySquad () {
      return this.squads[1];
    },
    loaded () {
      return !!this.homeSquad && !!this.awaySquad;
    },
    scheduleEvent () {
      if (this.isBracketRace) {
        return findScheduleEventByBracketSlot(this.league.schedule.events, this.bracketSlot);
      }
      return findScheduleEventByEncounter(this.league.schedule.events, this.squadIds, this.encounterNumber);
    },
    location () {
      if (this.isBracketRace) {
        return this.league.bracketSchedule[this.bracketSlot]?.locationId
          ? this.league.locations.find(l => l.id === this.league.bracketSchedule[this.bracketSlot].locationId)
          : this.homeSquad?.homeLocation;
      }
      return this.league.locations.find(l => l.id === this.scheduleEvent?.locationId);
    },
    tableNumber () {
      if (this.isBracketRace) {
        return this.league.bracketSchedule[this.bracketSlot]?.tableNumber ?? null;
      }
      return this.scheduleEvent?.tableNumber || null;
    },
    date () {
      if (this.isBracketRace) {
        return this.league.bracketSchedule[this.bracketSlot]?.date ?? null;
      }
      return this.scheduleEvent?.date;
    },
    week () {
      if (this.isBracketRace) {
        return this.league.bracketSchedule[this.bracketSlot]?.week ?? this.scheduleEvent?.week;
      }
      return this.scheduleEvent?.week;
    },
    bumped () {
      return !!(this.league.bracketSchedule[this.bracketSlot]?.bumped);
    },
    goalsBySquadId () {
      return goalsBySquadId(this.games, this.actualSquadIds);
    },
    homeGoals () {
      return this.homeSquad ? (this.goalsBySquadId[this.homeSquad.id] ?? 0) : 0;
    },
    awayGoals () {
      return this.awaySquad ? (this.goalsBySquadId[this.awaySquad.id] ?? 0) : 0;
    },
    decided () {
      return isDecided(this.goalsBySquadId, this.league.pointsToWin);
    },
    winningSquadId () {
      return winningSquadId(this.goalsBySquadId, this.league.pointsToWin);
    },
    winningSquadTitle () {
      if (!this.decided || !this.winningSquadId) return "";
      const squad = this.squads.find(s => s.id === this.winningSquadId);
      return squad ? squad.title : "";
    }
  },
  methods: {
    goalsBySquadIdExcludingThisGame (game) {
      const totals = { ...this.goalsBySquadId };
      if (game.scores) {
        for (const [id, score] of Object.entries(game.scores)) {
          if (Object.prototype.hasOwnProperty.call(totals, id)) {
            totals[id] -= score;
          }
        }
      }
      return totals;
    },
    remainingGoalsBySquadIdExcludingThisGame (game) {
      const totals = this.goalsBySquadIdExcludingThisGame(game);
      const remaining = {};
      for (const squad of this.squads) {
        remaining[squad.id] = this.league.pointsToWin - (totals[squad.id] || 0);
      }
      return remaining;
    },
    maxScoreForSquadInGame (squad, game) {
      const currentTotal = this.goalsBySquadIdExcludingThisGame(game)[squad.id] || 0;
      const max = this.league.pointsToWin - currentTotal;
      return Math.min(max, this.league.pointsPerGame);
    }
  }
};

export default defineReactiveModel(Race);